import { Check, ChevronRight, Circle } from "@baton/icons";
import type { ReactNode } from "react";
import { cn } from "../../cn.ts";
import { Kbd } from "../../primitives/display/Kbd/Kbd.tsx";
import { useStageBoundary } from "../portalContainer.tsx";

/**
 * The item shape DropdownMenu and ContextMenu share (§ Overlays): label, icon, shortcut,
 * danger, disabled, submenu, checkbox, radio. The rows are shadcn/ui's Dropdown Menu and
 * Context Menu items (the two sources are the same classes), themed through the variables of
 * `packages/tokens` (2026-09-15, by the owner's decision), and one renderer draws them for both
 * flavours from the Radix parts each supplies.
 *
 * What differs from the source, and why:
 * - a row is at least `--menu-item-height`, so compact density still steps it down (§ Density);
 * - a danger row takes the red text and tint tokens where the source takes `destructive`, the
 *   solid red, because the solid red as text on the raised surface is below 4.5:1 in the dark theme;
 * - the check and radio indicator, the inset and the submenu chevron use logical sides, so a
 *   right-to-left document mirrors them;
 * - the shortcut is the Kbd of § Primitives rather than tracked caption text, as every other
 *   shortcut in Baton is drawn.
 */
export type MenuItem =
  | { readonly kind?: "action"; readonly id: string; readonly label: string; readonly icon?: ReactNode; readonly shortcut?: string; readonly danger?: boolean; readonly disabled?: boolean; readonly onSelect: () => void }
  | { readonly kind: "checkbox"; readonly id: string; readonly label: string; readonly checked: boolean; readonly disabled?: boolean; readonly onCheckedChange: (checked: boolean) => void }
  | { readonly kind: "radio-group"; readonly id: string; readonly label?: string; readonly value: string; readonly options: readonly { value: string; label: string; icon?: ReactNode; disabled?: boolean }[]; readonly onValueChange: (value: string) => void }
  | { readonly kind: "submenu"; readonly id: string; readonly label: string; readonly icon?: ReactNode; readonly disabled?: boolean; readonly items: readonly MenuItem[] }
  | { readonly kind: "separator"; readonly id: string };

interface Slotted {
  readonly "data-slot"?: string;
}

/** The Radix parts a menu flavour supplies, so one renderer serves dropdown and context menus. */
export interface MenuParts {
  readonly Item: React.ComponentType<Slotted & { className?: string; disabled?: boolean; onSelect?: () => void; children?: ReactNode; "data-variant"?: string }>;
  readonly CheckboxItem: React.ComponentType<Slotted & { className?: string; disabled?: boolean; checked: boolean; onCheckedChange: (c: boolean) => void; children?: ReactNode }>;
  readonly RadioGroup: React.ComponentType<Slotted & { value: string; onValueChange: (v: string) => void; children?: ReactNode }>;
  readonly RadioItem: React.ComponentType<Slotted & { className?: string; disabled?: boolean; value: string; children?: ReactNode }>;
  readonly ItemIndicator: React.ComponentType<{ className?: string; children?: ReactNode }>;
  readonly Separator: React.ComponentType<Slotted & { className?: string }>;
  readonly Label: React.ComponentType<Slotted & { className?: string; children?: ReactNode }>;
  readonly Sub: React.ComponentType<Slotted & { children?: ReactNode }>;
  readonly SubTrigger: React.ComponentType<Slotted & { className?: string; disabled?: boolean; children?: ReactNode }>;
  readonly SubContent: React.ComponentType<Slotted & { className?: string; sideOffset?: number; collisionBoundary?: HTMLElement; children?: ReactNode }>;
  readonly Portal: React.ComponentType<{ children?: ReactNode }>;
}

/** A menu flavour: its Radix parts, its `data-slot` prefix and the classes of its panels, which read that flavour's Radix variables. */
export interface MenuFlavour {
  readonly parts: MenuParts;
  readonly slot: "dropdown-menu" | "context-menu";
  readonly subContentClassName: string;
}

const row =
  "relative flex min-h-(--menu-item-height) cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

const itemClassName = cn(
  row,
  "[&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:text-accent-red-text data-[variant=destructive]:focus:bg-accent-red-bg data-[variant=destructive]:focus:text-accent-red-text data-[variant=destructive]:*:[svg]:text-accent-red-text!",
);

const indicatedClassName = cn(row, "ps-8 pe-2");

const indicatorClassName = "pointer-events-none absolute start-2 flex size-3.5 items-center justify-center";

/** The panel classes both flavours share; each adds the origin, height and width variables its own Radix menu sets. */
export const menuPanelClassName =
  "z-(--layer-overlay) w-max min-w-(--menu-width-min) rounded-md border bg-popover p-1 text-popover-foreground duration-(--duration-base) data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95";

/** A submenu's panel, kept inside the overlay stage it opens on (`useStageBoundary`). */
function SubPanel({ flavour, children }: { readonly flavour: MenuFlavour; readonly children: ReactNode }) {
  const boundary = useStageBoundary();
  return (
    <flavour.parts.SubContent data-slot={`${flavour.slot}-sub-content`} className={flavour.subContentClassName} sideOffset={4} {...boundary}>
      {children}
    </flavour.parts.SubContent>
  );
}

function ItemLabel({ children }: { readonly children: ReactNode }) {
  return <span className="min-w-0 flex-1 truncate">{children}</span>;
}

export function renderItems(items: readonly MenuItem[], flavour: MenuFlavour): ReactNode {
  const { parts, slot } = flavour;
  return items.map((item) => {
    switch (item.kind) {
      case "separator":
        return <parts.Separator key={item.id} data-slot={`${slot}-separator`} className="-mx-1 my-1 h-px bg-border" />;
      case "checkbox":
        return (
          <parts.CheckboxItem key={item.id} data-slot={`${slot}-checkbox-item`} className={indicatedClassName} checked={item.checked} onCheckedChange={item.onCheckedChange} disabled={item.disabled ?? false}>
            <span className={indicatorClassName}>
              <parts.ItemIndicator>
                <Check aria-hidden="true" className="size-4" />
              </parts.ItemIndicator>
            </span>
            <ItemLabel>{item.label}</ItemLabel>
          </parts.CheckboxItem>
        );
      case "radio-group":
        return (
          <parts.RadioGroup key={item.id} data-slot={`${slot}-radio-group`} value={item.value} onValueChange={item.onValueChange}>
            {item.label !== undefined && (
              <parts.Label data-slot={`${slot}-label`} className="px-2 py-1.5 text-sm font-medium text-foreground">
                {item.label}
              </parts.Label>
            )}
            {item.options.map((option) => (
              <parts.RadioItem key={option.value} data-slot={`${slot}-radio-item`} className={indicatedClassName} value={option.value} disabled={option.disabled ?? false}>
                <span className={indicatorClassName}>
                  <parts.ItemIndicator>
                    <Circle aria-hidden="true" className="size-2 fill-current" />
                  </parts.ItemIndicator>
                </span>
                {option.icon !== undefined && (
                  <span className="inline-flex" aria-hidden="true">
                    {option.icon}
                  </span>
                )}
                <ItemLabel>{option.label}</ItemLabel>
              </parts.RadioItem>
            ))}
          </parts.RadioGroup>
        );
      case "submenu":
        return (
          <parts.Sub key={item.id} data-slot={`${slot}-sub`}>
            <parts.SubTrigger data-slot={`${slot}-sub-trigger`} className={cn(itemClassName, "data-[state=open]:bg-accent data-[state=open]:text-accent-foreground")} disabled={item.disabled ?? false}>
              {item.icon !== undefined && (
                <span className="inline-flex" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <ItemLabel>{item.label}</ItemLabel>
              <ChevronRight aria-hidden="true" className="ms-auto size-4 rtl:rotate-180" />
            </parts.SubTrigger>
            <parts.Portal>
              <SubPanel flavour={flavour}>{renderItems(item.items, flavour)}</SubPanel>
            </parts.Portal>
          </parts.Sub>
        );
      case "action":
      case undefined: {
        return (
          <parts.Item key={item.id} data-slot={`${slot}-item`} data-variant={item.danger === true ? "destructive" : "default"} className={itemClassName} disabled={item.disabled ?? false} onSelect={item.onSelect}>
            {item.icon !== undefined && (
              <span className="inline-flex" aria-hidden="true">
                {item.icon}
              </span>
            )}
            <ItemLabel>{item.label}</ItemLabel>
            {item.shortcut !== undefined && (
              <span data-slot={`${slot}-shortcut`} className="ms-auto inline-flex ps-4">
                <Kbd keys={item.shortcut} />
              </span>
            )}
          </parts.Item>
        );
      }
      default: {
        const exhaustive: never = item;
        throw new Error(`unknown menu item ${String(exhaustive)}`);
      }
    }
  });
}
